import { useState, useEffect, useCallback, useMemo } from 'react';
import { Measure, AudioSettings, TimeSignature, KeySignature, UISettings, JamSessionState, JamSessionRole } from '../types';
import { FreemiumMessageKey } from '../utils/freemiumLimits';
import { FREEMIUM_LIMITS } from '../utils/freemiumLimits';
import webRTCService, { WebRTCCallbacks, PeerInfo } from '../utils/webrtc';
import type { User, UserCredential } from 'firebase/auth';

export interface ChartStateForJam {
  songTitle: string;
  songAuthor: string;
  songStyle: string;
  sourceChart: Measure[];
  numberOfMeasures: number;
  timeSignature: TimeSignature;
  keySignature: KeySignature;
  transposeAmount: number;
  audioSettings: AudioSettings;
  uiSettings: UISettings;
  setSongTitle: (v: string) => void;
  setSongAuthor: (v: string) => void;
  setSongStyle: (v: string) => void;
  setSourceChart: (v: Measure[]) => void;
  setNumberOfMeasures: (v: number) => void;
  setTimeSignature: (v: TimeSignature) => void;
  setKeySignature: (v: KeySignature) => void;
  setTransposeAmount: (v: number) => void;
  setAudioSettings: (v: AudioSettings | ((prev: AudioSettings) => AudioSettings)) => void;
  setUiSettings: (v: UISettings | ((prev: UISettings) => UISettings)) => void;
}

export interface UseJamSessionReturn {
  // State (owned by this hook)
  joinSessionCode: string;
  setJoinSessionCode: React.Dispatch<React.SetStateAction<string>>;
  isJoiningSession: boolean;
  isQRCodeLoading: boolean;
  connectedPeers: PeerInfo[];
  webRTCError: string | null;
  setWebRTCError: React.Dispatch<React.SetStateAction<string | null>>;

  // Handlers
  handleCreateJamSession: () => Promise<void>;
  handleJoinJamSession: () => Promise<void>;
  handleLeaveJamSession: () => void;
  handleCloseJamDialog: () => void;
}

export interface JamSessionExternalState {
  jamSessionState: JamSessionState;
  setJamSessionState: React.Dispatch<React.SetStateAction<JamSessionState>>;
  jamSessionRole: JamSessionRole;
  setJamSessionRole: React.Dispatch<React.SetStateAction<JamSessionRole>>;
  jamSessionCode: string;
  setJamSessionCode: React.Dispatch<React.SetStateAction<string>>;
}

export function useJamSession(
  jamState: JamSessionExternalState,
  chartState: ChartStateForJam,
  user: User | null,
  signInAnonymously: () => Promise<UserCredential | null | undefined>,
  isPremium: boolean,
  isLoading: boolean,
  setShowWelcome: (v: boolean) => void,
  closeJamSessionDialog: () => void,
  setFeatureLimitMessageKey: (key: FreemiumMessageKey | undefined) => void,
  setFeatureLimitModalOpen: (v: boolean) => void,
): UseJamSessionReturn {
  const {
    jamSessionState, setJamSessionState,
    jamSessionRole, setJamSessionRole,
    setJamSessionCode: setJamCode,
  } = jamState;

  const {
    songTitle, songAuthor, songStyle, sourceChart, numberOfMeasures,
    timeSignature, keySignature, transposeAmount, audioSettings, uiSettings,
    setSongTitle, setSongAuthor, setSongStyle, setSourceChart,
    setNumberOfMeasures, setTimeSignature, setKeySignature,
    setTransposeAmount, setAudioSettings, setUiSettings,
  } = chartState;

  const [joinSessionCode, setJoinSessionCode] = useState('');
  const [isJoiningSession, setIsJoiningSession] = useState(false);
  const [isQRCodeLoading, setIsQRCodeLoading] = useState(false);
  const [connectedPeers, setConnectedPeers] = useState<PeerInfo[]>([]);
  const [webRTCError, setWebRTCError] = useState<string | null>(null);

  // Update WebRTC auth token when user auth state changes
  useEffect(() => {
    const updateWebRTCToken = async () => {
      if (user) {
        try {
          const token = await user.getIdToken();
          webRTCService.setAuthToken(token);
          console.log('✅ WebRTC auth token updated');
        } catch (error) {
          console.error('❌ Failed to get Firebase ID token:', error);
          webRTCService.setAuthToken(null);
        }
      } else {
        webRTCService.setAuthToken(null);
        console.log('🔒 WebRTC auth token cleared (user logged out)');
      }
    };
    updateWebRTCToken();
  }, [user]);

  // QR Code Auto-Join from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlJoinCode = urlParams.get('join');

    if (urlJoinCode && !isLoading && jamSessionState === 'idle') {
      console.log('🔍 Found join parameter in URL:', urlJoinCode);
      window.history.replaceState({}, document.title, window.location.pathname);

      setIsQRCodeLoading(true);
      setShowWelcome(false);
      setJoinSessionCode(urlJoinCode.toUpperCase());

      setTimeout(async () => {
        try {
          setIsJoiningSession(true);
          setJamSessionRole('client');
          setWebRTCError(null);

          if (!user) {
            console.log('👤 QR Code join: No user - signing in anonymously...');
            const userCredential = await signInAnonymously();
            if (userCredential?.user) {
              const token = await userCredential.user.getIdToken();
              webRTCService.setAuthToken(token);
              console.log('✅ WebRTC auth token set after anonymous sign in (QR code)');
            }
          } else {
            const token = await user.getIdToken();
            webRTCService.setAuthToken(token);
            console.log('✅ WebRTC auth token set for existing user (QR code)');
          }

          await webRTCService.joinSession(urlJoinCode.toUpperCase());
          setJamCode(urlJoinCode.toUpperCase());
          closeJamSessionDialog();
          setJoinSessionCode('');
          setIsJoiningSession(false);

          console.log('✅ Auto-joined jam session from QR code:', urlJoinCode);
        } catch (error) {
          console.error('❌ Failed to auto-join jam session:', error);
          setWebRTCError('Failed to join session from QR code');
          setJamSessionState('idle');
          setJamSessionRole(null);
          setIsJoiningSession(false);
          setIsQRCodeLoading(false);
        }
      }, 100);
    }
  }, [isLoading, jamSessionState, user, signInAnonymously]);

  // WebRTC Callbacks for real-time collaboration
  const webRTCCallbacks: WebRTCCallbacks = useMemo(() => ({
    onStateChange: (state) => {
      console.log('🔄 WebRTC state changed:', state, '- Current jamSessionRole:', jamSessionRole);
      if (state === 'connected') {
        setJamSessionState('connected');
        setIsQRCodeLoading(false);
      } else if (state === 'fallback-server') {
        setJamSessionState('fallback-server');
        setIsQRCodeLoading(false);
      } else if (state === 'hosting') {
        setJamSessionState('hosting');
      } else if (state === 'joining') {
        setJamSessionState('joining');
      } else if (state === 'error' || state === 'disconnected' || state === 'idle') {
        setJamSessionState('idle');
        setJamSessionRole(null);
      }
    },
    onPeerJoin: (peerInfo) => {
      console.log('Peer joined:', peerInfo);

      if (jamSessionRole === 'host' && !isPremium) {
        const currentParticipants = connectedPeers.length + 1;
        if (currentParticipants >= FREEMIUM_LIMITS.FREE_MAX_JAM_PARTICIPANTS) {
          console.log('🚫 Free tier: Maximum participants reached');
          setFeatureLimitMessageKey('JAM_PARTICIPANTS_LIMIT');
          setFeatureLimitModalOpen(true);
        }
      }

      setConnectedPeers(prev => [...prev.filter(p => p.id !== peerInfo.id), peerInfo]);

      if (jamSessionRole === 'host') {
        const currentChart = {
          songTitle, songAuthor, sourceChart, numberOfMeasures,
          timeSignature, keySignature, transposeAmount, audioSettings, uiSettings
        };
        webRTCService.broadcastFullChart(currentChart);
      }
    },
    onPeerLeave: (peerId) => {
      console.log('Peer left:', peerId);
      setConnectedPeers(prev => prev.filter(p => p.id !== peerId));
    },
    onChartUpdate: (chartData) => {
      console.log('📊 Received chart update:', chartData);
      if (jamSessionRole === 'client') {
        if (chartData.sourceChart) setSourceChart(chartData.sourceChart);
        if (chartData.songTitle) setSongTitle(chartData.songTitle);
        if (chartData.songAuthor) setSongAuthor(chartData.songAuthor);
        if (chartData.songStyle) setSongStyle(chartData.songStyle);
        if (chartData.numberOfMeasures) setNumberOfMeasures(chartData.numberOfMeasures);
        if (chartData.timeSignature) setTimeSignature(chartData.timeSignature);
        if (chartData.keySignature) setKeySignature(chartData.keySignature);
        if (chartData.transposeAmount !== undefined) setTransposeAmount(chartData.transposeAmount);
        if (chartData.audioSettings) setAudioSettings(chartData.audioSettings);
      }
    },
    onChartFullSync: (chartData) => {
      console.log('📊 Received full chart sync:', chartData);
      if (jamSessionRole === 'client') {
        setSongTitle(chartData.songTitle);
        setSongAuthor(chartData.songAuthor || '');
        setSongStyle(chartData.songStyle || 'Jazz');
        setSourceChart(chartData.sourceChart);
        setNumberOfMeasures(chartData.numberOfMeasures);
        setTimeSignature(chartData.timeSignature);
        setKeySignature(chartData.keySignature);
        setTransposeAmount(chartData.transposeAmount);
        if (chartData.audioSettings) setAudioSettings(chartData.audioSettings);
        if (chartData.uiSettings) setUiSettings(chartData.uiSettings);
      }
    },
    onError: (error) => {
      console.error('WebRTC error:', error);
      setWebRTCError(error);
    }
  }), [jamSessionRole, songTitle, songAuthor, songStyle, sourceChart, numberOfMeasures,
       timeSignature, keySignature, transposeAmount, audioSettings, uiSettings]);

  // Initialize WebRTC callbacks
  useEffect(() => {
    webRTCService.setCallbacks(webRTCCallbacks);
  }, [webRTCCallbacks]);

  // Set up chart sync heartbeat callback for fallback mode
  useEffect(() => {
    if (jamSessionRole === 'host') {
      webRTCService.setChartSyncCallback(() => {
        const currentChart = {
          songTitle, songAuthor, songStyle, sourceChart, numberOfMeasures,
          timeSignature, keySignature, transposeAmount, audioSettings, uiSettings
        };
        webRTCService.broadcastFullChart(currentChart);
      });
    }
  }, [jamSessionRole, songTitle, songAuthor, songStyle, sourceChart, numberOfMeasures,
      timeSignature, keySignature, transposeAmount, audioSettings, uiSettings]);

  // Listen for state-based sync requests from WebRTC service
  useEffect(() => {
    const handleFullSyncRequest = () => {
      if (jamSessionRole === 'host') {
        const currentChart = {
          songTitle, songAuthor, songStyle, sourceChart, numberOfMeasures,
          timeSignature, keySignature, transposeAmount, audioSettings, uiSettings
        };
        webRTCService.broadcastFullChart(currentChart);
      }
    };
    window.addEventListener('webrtc-request-full-sync', handleFullSyncRequest);
    return () => window.removeEventListener('webrtc-request-full-sync', handleFullSyncRequest);
  }, [jamSessionRole, songTitle, songAuthor, songStyle, sourceChart, numberOfMeasures,
      timeSignature, keySignature, transposeAmount, audioSettings, uiSettings]);

  // Broadcast chart changes to connected clients (host only)
  useEffect(() => {
    if (jamSessionRole === 'host' && (jamSessionState === 'connected' || jamSessionState === 'fallback-server')) {
      webRTCService.broadcastChartUpdate({ sourceChart });
    }
  }, [sourceChart, jamSessionRole, jamSessionState]);

  useEffect(() => {
    if (jamSessionRole === 'host' && (jamSessionState === 'connected' || jamSessionState === 'fallback-server')) {
      webRTCService.broadcastChartUpdate({ songTitle, songAuthor, songStyle });
    }
  }, [songTitle, songAuthor, songStyle, jamSessionRole, jamSessionState]);

  useEffect(() => {
    if (jamSessionRole === 'host' && (jamSessionState === 'connected' || jamSessionState === 'fallback-server')) {
      webRTCService.broadcastChartUpdate({ numberOfMeasures, timeSignature, keySignature });
    }
  }, [numberOfMeasures, timeSignature, keySignature, jamSessionRole, jamSessionState]);

  useEffect(() => {
    if (jamSessionRole === 'host' && (jamSessionState === 'connected' || jamSessionState === 'fallback-server')) {
      webRTCService.broadcastChartUpdate({ transposeAmount });
    }
  }, [transposeAmount, jamSessionRole, jamSessionState]);

  useEffect(() => {
    if (jamSessionRole === 'host' && (jamSessionState === 'connected' || jamSessionState === 'fallback-server')) {
      webRTCService.broadcastChartUpdate({ audioSettings });
    }
  }, [audioSettings, jamSessionRole, jamSessionState]);

  // Handlers
  const handleCreateJamSession = useCallback(async () => {
    try {
      console.log('🎵 Creating jam session...');
      if (!user) {
        const userCredential = await signInAnonymously();
        if (userCredential?.user) {
          const token = await userCredential.user.getIdToken();
          webRTCService.setAuthToken(token);
        }
      } else {
        const token = await user.getIdToken();
        webRTCService.setAuthToken(token);
      }

      setJamSessionRole('host');
      setWebRTCError(null);

      const sessionCode = await webRTCService.createSession();
      setJamCode(sessionCode);
      closeJamSessionDialog();
      setJoinSessionCode('');
    } catch (error) {
      console.error('❌ Failed to create jam session:', error);
      alert(`Failed to create jam session: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setWebRTCError('Failed to create session');
      setJamSessionState('idle');
      setJamSessionRole(null);
    }
  }, [user, signInAnonymously, closeJamSessionDialog]);

  const handleJoinJamSession = useCallback(async () => {
    if (!joinSessionCode.trim()) return;
    try {
      setIsJoiningSession(true);
      if (!user) {
        const userCredential = await signInAnonymously();
        if (userCredential?.user) {
          const token = await userCredential.user.getIdToken();
          webRTCService.setAuthToken(token);
        } else {
          throw new Error('Anonymous sign in failed - no user returned');
        }
      } else {
        const token = await user.getIdToken();
        webRTCService.setAuthToken(token);
      }

      setJamSessionRole('client');
      setWebRTCError(null);

      await webRTCService.joinSession(joinSessionCode.toUpperCase());
      setJamCode(joinSessionCode.toUpperCase());
      closeJamSessionDialog();
      setJoinSessionCode('');
      setIsJoiningSession(false);
    } catch (error) {
      console.error('❌ Failed to join jam session:', error);
      setWebRTCError(`Failed to join session: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setJamSessionState('idle');
      setJamSessionRole(null);
      setIsJoiningSession(false);
    }
  }, [joinSessionCode, user, signInAnonymously, closeJamSessionDialog]);

  const handleLeaveJamSession = useCallback(() => {
    webRTCService.leaveSession();
    setJamSessionState('idle');
    setJamSessionRole(null);
    setJamCode('');
    setConnectedPeers([]);
    setWebRTCError(null);
  }, []);

  const handleCloseJamDialog = useCallback(() => {
    closeJamSessionDialog();
    setJoinSessionCode('');
    setIsJoiningSession(false);
  }, [closeJamSessionDialog]);

  return {
    joinSessionCode,
    setJoinSessionCode,
    isJoiningSession,
    isQRCodeLoading,
    connectedPeers,
    webRTCError,
    setWebRTCError,
    handleCreateJamSession,
    handleJoinJamSession,
    handleLeaveJamSession,
    handleCloseJamDialog,
  };
}
